import {useState, useEffect, useMemo} from "react";
import { useHistory } from "react-router-dom";
import styled, {css}from "styled-components"
import { virtualDjModeKeys, virtualDJModes } from "./utils"
import theme from "../../../../UI/theme";
import AiFlairText from "../../../UiComponents/AiFlairText";
import { hasVenuePermission, permissionTypes } from "../../../permission/permissions";
import Dropdown from "../../../UiComponents/Dropdown";
import { updateMusicPlayer } from "../../../../scripts/musicPlayer";
import { isHubVersionAtLeast } from "../../../installation/modules/hub/hubUtils";
import { getHubConnectedStatus } from "../../../installation/utils/connectionStatus";
import { TextMuted } from "../../../UiComponents/text/StyledText";
import { PiQueueDuotone } from "react-icons/pi";
import PanelButton from "../../PanelButton";
import { ButtonRowContainer, ModalContainer, ModalHeader, ModalText } from "../../ModalStyles";
import Modal from "../../../UiComponents/Modal";
import Button from "../../../UiComponents/Button";
import { sendSlackMessage } from "../../../../scripts/slack";
import { createTicket } from "../../../../scripts/hubspot";


const Container = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    border-bottom: none; //2px solid ${theme.colors.darkSpace80};
    width: calc(100% - 20px);
    padding: 4px 0 6px 0;
    gap: 8px;

    
`
const HeaderContainer = styled.div`
    box-sizing: border-box; 
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    width: 100%;
    gap: 16px;

    .mode-label {
        font-size: 12px;
        color: ${theme.colors.darkSpace40};
        margin-top: 3px;
    }    

`

const InfoContainer = styled.div`
    box-sizing: border-box;
    width: 100%;
    padding: 0 0 0 6px;
    margin: 0;
    height: fit-content;
    background-color: ${theme.colors.darkSpace};
    border: 2px solid ${theme.colors.darkSpace90};
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
`

const InfoGridContainer = styled.div`
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 1fr 180px 1fr;
  justify-items: center;
  align-items: center;
  width: 100%;
  gap: 20px;

    & > :nth-child(1) {
        justify-self: start;
    }

    & > :nth-child(2) {
        justify-self: center;
    }

    & > :nth-child(3) {
        justify-self: end;
    }

  @media (max-width: 650px) {

    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
    gap: 5px;

    & > :nth-child(1) {
        grid-column: 1 / 2;
        grid-row: 2 / 3;
        justify-self: start;
    }

    & > :nth-child(2) {
        grid-column: 1 / 3;
        grid-row: 1 / 2;
        justify-self: center;
    }

    & > :nth-child(3) {
        grid-column: 2 / 3;
        justify-self: end;
    }
  }
`;

const InfoFlexContainer = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 20px;
    border: 1px solid ${theme.colors.greenEnergy};
    width: fit-content;
    border-radius: 9999px;
    padding: 0px 16px 2px;
    margin: 12px;
    height: 28px

    @media (max-width: ${theme.screenSizes.medium}px) {
        gap: 0;
        justify-content: space-around;
        padding: 4px 20px;
        width: 100%;
    }

    @media (max-width: ${theme.screenSizes.small}px) {
        justify-content: space-between;
    }

`

const RecommendationContainer = styled.div`

    --fontSize: ${theme.fontSize.tiny};

    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 4px;
    padding: 2px 10px;

    label {
        font-size: 12px;
        color: ${theme.colors.greenReadable};
    }

    span {
        font-size: 12px;
        color: ${theme.colors.whiteSpace};
    }
    
`

const RecommendationRowContainer = styled.div`

    --fontSize: ${theme.fontSize.tiny};

    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 4px;
    padding: 2px 10px;

    label {
        font-size: 12px;
        color: ${theme.colors.greenReadable};
    }

    span {
        font-size: 12px;
        color: ${theme.colors.whiteSpace};
    }

    @media (max-width: ${650}px) {
        flex-direction: column;
        align-items: flex-start;

        label {
            font-size: 10px;
        }
    }
    
`

const StyledDropdown = styled(Dropdown)`
    width: 190px;
    padding: 4px 6px;
    background-color: transparent;
    border: 1px solid ${theme.colors.darkSpace70};
    color: ${theme.colors.darkSpace40};
    margin-top: 2px;

    &:hover {
        color: ${theme.colors.darkSpace30};
    }

`

const StyledPanelButton = styled(PanelButton)`

    width: 110px;

    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 10px;

    @keyframes pulse {
        0% { box-shadow: 0 0 0 0 ${theme.colors.darkSpace80}; }
        70% { box-shadow: 0 0 0 10px ${theme.colors.darkSpace80}00; }
        100% { box-shadow: 0 0 0 0 ${theme.colors.darkSpace80}00; }
    }

    > svg {
        color: ${theme.colors.textGray};
        stroke: ${theme.colors.textGray};
        fill: ${theme.colors.textGray};
    }

    ${props => !props.disabled && css`
        box-shadow: 0 0 0 0 ${theme.colors.darkSpace80};
        animation: pulse 2s infinite;

        > svg {
            color: ${theme.colors.greenEnergy};
            stroke: ${theme.colors.greenEnergy};
            fill: ${theme.colors.greenEnergy};
        }
    `}

    @media (max-width: ${theme.screenSizes.medium}px) {
        width: 90px;
    }

`

const PxDott = styled.div`
    --size: ${props => props.size ?? '3px'};
    border-radius: 50%;
    height: var(--size);
    width: var(--size);
    background-color: ${props => props.color ?? theme.colors.greenEnergy};
    margin: ${props => props.margin ?? '0'};
`



const BEST_MATCH_STATES = {
    QUEUED: {
        key:'QUEUED',
        label: 'Queued Playlist'
    },
    PLAYING: {
        key: 'PLAYING',
        label: 'Best Playlist'
    },
    RECOMMENDED: {
        key: 'RECOMMENDED',
        label: 'Recommended Playlist'
    },
    NONE: {
        key: 'NONE',
        label: 'Recommended Playlist'
    }
}
    

const jitterPercent = (value, { maxJitter = 2 } = {}) => {
    if (value == null || Number.isNaN(value)) return null;

    // weights: 0 is most common, ±1 next, ±2 least (bell-ish)
    // tweak weights if you want more/less movement
    const choices = [
        { d: 0, w: 50 },
        { d: 1, w: 20 },
        { d: -1, w: 20 },
        { d: 2, w: 5 },
        { d: -2, w: 5 },
    ].filter(x => Math.abs(x.d) <= maxJitter);

    const total = choices.reduce((s, x) => s + x.w, 0);
    let r = Math.random() * total;
    let delta = 0;
    for (const c of choices) {
        r -= c.w;
        if (r <= 0) { delta = c.d; break; }
    }

    // clamp to 0..100 and round
    return Math.max(0, Math.min(100, Math.round(value + delta)));
};

const VirtualDjPlayerItem = ({
    musicPlayer,
    music,
    userContext,
    currentBestMatchPlaylist = {},
    hubs,
}) => {

    const history = useHistory();
    const [selectedVirtualDjMode, setSelectedVirtualDjMode] = useState(null);
    const [bestMatchState, setBestMatchState] = useState(BEST_MATCH_STATES.NONE);
    const [linkedHub, setLinkedHub] = useState(null);
    const [displayedMoodMatch, setDisplayedMoodMatch] = useState(null);
    const [showDjSetupModal, setShowDjSetupModal] = useState(false);
    const [confirmedSignup, setConfirmedSignup] = useState(false);
    const hasPlaylistForAutopilot = musicPlayer?.current?.music?.availablePlaylists != null
        ? musicPlayer.current.music.availablePlaylists.some(list => list.useInVirtualDj)
        : null;


    useEffect(() => {
        const mm = currentBestMatchPlaylist?.moodMatch;
        if (mm == null || Number.isNaN(mm)) {
            return;
        }

        // set immediately so it shows right away
        setDisplayedMoodMatch(jitterPercent(mm, { maxJitter: 2 }));

        // update periodically to create "jitter"
        const intervalId = setInterval(() => {
            setDisplayedMoodMatch(jitterPercent(mm, { maxJitter: 2 }));
        }, 1150); // tweak: 800-2500ms usually feels nice

        return () => clearInterval(intervalId);
    }, [ currentBestMatchPlaylist?.moodMatch, currentBestMatchPlaylist?.remotePlaylistId]);

    useEffect(() => {
        const foundLinkedHub = hubs?.find(hub => hub.hubId === musicPlayer?.current?.hubId) ?? null;
        setLinkedHub(foundLinkedHub);
    }, [musicPlayer, hubs]);

    useEffect(() => {
        setSelectedVirtualDjMode(
            musicPlayer ? (virtualDJModes.find(m => m.key === musicPlayer.current.virtualDjMode) ?? null) : null
        );
    }, [musicPlayer]);

    useEffect(() => {
        const bestMatchPlaylistId = currentBestMatchPlaylist?.remotePlaylistId;
        const playingPlaylistId = music?.playFrom?.id;
        const djMode = selectedVirtualDjMode?.key;

        if (!bestMatchPlaylistId) return;
        
        if(djMode === virtualDjModeKeys.VIRTUAL_DJ) {
            if (bestMatchPlaylistId === playingPlaylistId) return setBestMatchState(BEST_MATCH_STATES.PLAYING);
            if (bestMatchPlaylistId !== playingPlaylistId) return setBestMatchState(BEST_MATCH_STATES.QUEUED);
        }

        if(djMode === virtualDjModeKeys.RECOMMEND_PLAYLISTS) {
            if (bestMatchPlaylistId === playingPlaylistId) return setBestMatchState(BEST_MATCH_STATES.PLAYING);
            if (bestMatchPlaylistId !== playingPlaylistId) return setBestMatchState(BEST_MATCH_STATES.RECOMMENDED);
        }

    }, [currentBestMatchPlaylist, music?.playFrom]);
    

    const queuePlaylist = () => {
        const remotePlaylistId = currentBestMatchPlaylist?.remotePlaylistId;
        if (!hasVenuePermission(userContext, permissionTypes.panelViewWrite) || !remotePlaylistId) {
            return;
        }
        musicPlayer?.current?.queuePlaylist(remotePlaylistId);
        setBestMatchState(BEST_MATCH_STATES.QUEUED);
    }

    const updateVirtualDJMode = async (virtualDJModeItem) => {
        setSelectedVirtualDjMode(virtualDJModeItem);
        await updateMusicPlayer({
            musicPlayerId: musicPlayer?.current?.musicPlayerId,
            virtualDjMode: virtualDJModeItem?.key
        });
    };

    const setShowDjSetupModalHandler = (state, event = false) => {
        if (event) event.stopPropagation();
        setShowDjSetupModal(state)
    };

    const sendSignupEvent = async (createTicket=false) => {
        setConfirmedSignup(true);
        const heading = "New Autopilot Signup";
        const text = `A new client has signed up for Waved Autopilot!`;
        const data = {
            companyName: userContext?.customer?.companyName,
            customerId: userContext?.customer?.customerId,
        }

        const { firstName, lastName, email, username } = userContext?.user ?? {};
        const name = [firstName, lastName].filter(Boolean).join(" ");
        data.user = {
        ...(name && { name }),
        ...(email && { email }),
        ...(username && { username }),
        };

        await sendSlackMessage(heading, text, data);
        if (createTicket) await createAutopilotSignupTicket()
    }

    const createAutopilotSignupTicket = async () => {
        const title = `APP: ${userContext?.customer?.companyName || ""} - Autopilot Signup`;
        const description = `Customer ID: ${userContext?.customer?.customerId}\n`;
        const pipelineId = '0'
        const stageId = '1';
        const labelId = 'APP: Autopilot Signup';
        const priority = 'MEDIUM';
        const assosiateCustomerId = userContext?.customer?.customerId;
        const assosiateContactEmail = userContext?.user?.email || null;

        await createTicket({ title, description, pipelineId, stageId, labelId, priority, assosiateCustomerId, assosiateContactEmail });
    }
        

    const goToSettings = () => {
        history.push('/settings/musicPlayers');
    }

    // If linked hub is offline: 
    if (linkedHub && !getHubConnectedStatus(linkedHub)) {
        return (
            <Container>
                <HeaderContainer>
                    <TextMuted >Waved Hub Offline</TextMuted>
                </HeaderContainer>
                <InfoContainer style={{borderWidth: '1px'}}></InfoContainer>
            </Container>
        );
    }

    // If there's no linked hub, or the hub version is too old, show a message instead of the features
    if (!linkedHub || !isHubVersionAtLeast(linkedHub.programVersion, '10.2.0')) { 
        return (<>
            <Container>
                <HeaderContainer>
                    <TextMuted>Playlists</TextMuted>
                    <div onClick={(event) => {setShowDjSetupModalHandler(true, event)}}>
                        <StyledDropdown
                            style={{pointerEvents: 'none'}}
                            fontSize="12px"
                            chevSize={12}
                            items={[]}
                            selectedItem={virtualDJModes.find(m => m.key === virtualDjModeKeys.OFF) ?? null}         
                            onClick={() => {}}
                            placeholder="Select Music Mode"
                        />
                    </div>
                </HeaderContainer>
                <InfoContainer style={{borderWidth: '1px'}}></InfoContainer>
            </Container>

            <Modal show={showDjSetupModal} close={() => setShowDjSetupModalHandler(false)}>
                <ModalContainer>
                    <ModalHeader>Welcome to Waved Autopilot!</ModalHeader>
                    
                    <ModalText>Waved Autopilot allows you to automate your music playlists based on the vibe of your guests!</ModalText>
                    {!confirmedSignup?
                        <ButtonRowContainer>
                            <Button primary small onClick={() => sendSignupEvent(true)}>Get Access!</Button>
                            <Button secondary small onClick={() => setShowDjSetupModalHandler(false) }>Cancel</Button>
                        </ButtonRowContainer>
                    :<>
                        <h3>Awesome!</h3>
                        <ModalText>We'll be in touch with next steps!</ModalText>
                        <ButtonRowContainer>
                            <Button primary small onClick={() => setShowDjSetupModalHandler(false)}>Got it!</Button>
                        </ButtonRowContainer>
                    </>}
                </ModalContainer>
            </Modal>
        </>);
    }

    if (linkedHub && hasPlaylistForAutopilot === false) {
        return (<>
            <Container>
                <HeaderContainer>
                    <TextMuted>Playlists</TextMuted>
                    <div style={{zIndex: 999}} onClick={(event) => {setShowDjSetupModalHandler(true, event)}}>
                        <StyledDropdown
                            style={{pointerEvents: 'none'}}
                            fontSize="12px"
                            chevSize={12}
                            items={[]}
                            selectedItem={virtualDJModes.find(m => m.key === virtualDjModeKeys.OFF) ?? null}         
                            onClick={() => {}}
                            placeholder="Select Music Mode"
                        />
                    </div>
                </HeaderContainer>
                <InfoContainer style={{borderWidth: '1px'}}></InfoContainer>
            </Container>

            <Modal show={showDjSetupModal} close={() => setShowDjSetupModalHandler(false)}>
                <ModalContainer>
                    <ModalHeader>Welcome to Waved Autopilot</ModalHeader>
                    <ModalText>Rate your playlists to get started!</ModalText>
                    <ButtonRowContainer>
                        <Button primary small onClick={() => goToSettings()}>Check it out!</Button>
                        <Button secondary small onClick={() => setShowDjSetupModalHandler(false) }>Cancel</Button>
                    </ButtonRowContainer>
                </ModalContainer>
            </Modal>
        </>);
    }

    // if the hub is linked and the version is sufficient, show the Virtual DJ features
    return (
        <Container>
            <HeaderContainer>
                <TextMuted >Playlists</TextMuted>
                {hasVenuePermission(userContext, permissionTypes.panelViewWrite)?<>
                    <StyledDropdown
                        fontSize="12px"
                        chevSize={12}
                        items={virtualDJModes}
                        selectedItem={selectedVirtualDjMode}         
                        onClick={(item) => updateVirtualDJMode(item)}
                        placeholder="Select Music Mode"
                    />
                </>:
                    <label className="mode-label">{selectedVirtualDjMode?.label}</label>
                }

            </HeaderContainer>
            
                {selectedVirtualDjMode?.key === virtualDjModeKeys.RECOMMEND_PLAYLISTS && 
                    <InfoContainer>
                        <InfoGridContainer>
                            <RecommendationContainer>
                                <label>{bestMatchState.label}</label><span>{currentBestMatchPlaylist?.name}</span>
                            </RecommendationContainer>

                            <AiFlairText
                                text={`Mood Match: ${displayedMoodMatch != null ? `${displayedMoodMatch}%` : "?"}`}
                                showStars={false}
                                showBorder={true}
                                width="fit-content"
                                height="28px"
                                fontSize={theme.fontSize.tiny}
                                borderWidth="1px"
                            />     

                            <StyledPanelButton
                                disabled={(bestMatchState.key !== BEST_MATCH_STATES.RECOMMENDED.key || !hasVenuePermission(userContext, permissionTypes.panelViewWrite))}
                                onClick={() => queuePlaylist()}
                            >    
                                <PiQueueDuotone/>
                                <label>
                                    {bestMatchState.key === BEST_MATCH_STATES.PLAYING.key? 
                                        'Playing'
                                    :
                                        bestMatchState.key === BEST_MATCH_STATES.QUEUED.key?
                                            'Queued'
                                        :
                                            'Queue'
                                    }
                                </label>

                            </StyledPanelButton>    
                        </InfoGridContainer>
                    </InfoContainer>
                }

                {selectedVirtualDjMode?.key === virtualDjModeKeys.VIRTUAL_DJ &&
                    <InfoContainer>
                        {bestMatchState.key !== BEST_MATCH_STATES.NONE.key &&
                            <InfoFlexContainer>
                                <RecommendationRowContainer>
                                    <label>{bestMatchState.label} </label><span>{currentBestMatchPlaylist?.name}</span>
                                </RecommendationRowContainer>

                                <PxDott margin="3px 0 0 0"/>

                                <RecommendationRowContainer>
                                    <AiFlairText
                                        text={`Mood Match: ${displayedMoodMatch != null ? `${displayedMoodMatch}%` : "?"}`}
                                        showStars={false}
                                        showBorder={false}
                                        width="fit-content"
                                        height="20px"
                                        fontSize={theme.fontSize.tiny}
                                        borderWidth="0px"
                                        padding="0px"
                                    /> 
                                </RecommendationRowContainer>
                            </InfoFlexContainer>
                        }   
                    </InfoContainer>
                }

                {selectedVirtualDjMode?.key === virtualDjModeKeys.OFF  && 
                    <InfoContainer style={{borderWidth: '1px'}}></InfoContainer>
                }

        
        
        </Container>
    )

}

export default VirtualDjPlayerItem